import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/config/routes';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { 
  Users, 
  CheckSquare, 
  AlertTriangle, 
  UserCheck,
  Clock,
  TrendingUp,
  Briefcase,
  ChevronRight,
  CheckCircle2,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';

/**
 * Depot Home Dashboard - "Today at a Glance"
 * Shows current site status, pending tasks, and quick actions
 */
const DepotHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [occupancy, setOccupancy] = useState({ total: 0, employees: 0, contractors: 0, visitors: 0 });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingProjectReviews, setPendingProjectReviews] = useState([]);
  const [openIncidents, setOpenIncidents] = useState(0);
  const [activeSessions, setActiveSessions] = useState([]);
  const [todayCheckIns, setTodayCheckIns] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.depot_id) {
      loadDashboardData();
    } else if (user !== undefined) {
      setLoading(false);
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get active sessions for current occupancy
      const { data: sessions, error: sessionsError } = await supabase
        .from('site_sessions')
        .select('*')
        .eq('depot_id', user.depot_id)
        .is('checked_out_at', null)
        .order('checked_in_at', { ascending: false });

      if (sessionsError) throw sessionsError;

      // Calculate occupancy breakdown
      const employees = sessions?.filter(s => s.person_type === 'employee').length || 0;
      const contractors = sessions?.filter(s => s.person_type === 'contractor').length || 0;
      const visitors = sessions?.filter(s => s.person_type === 'visitor').length || 0;
      
      setOccupancy({
        total: sessions?.length || 0,
        employees,
        contractors,
        visitors
      });
      setActiveSessions(sessions || []);

      // Get pending approvals (standalone only — project team submissions are handled
      // via the project approval flow and must not appear in the standalone queue)
      const { data: allApprovals, error: approvalsError } = await supabase
        .from('induction_submissions')
        .select('*, induction_briefs!inner(depot_id)')
        .eq('status', 'submitted')
        .eq('induction_briefs.depot_id', user.depot_id)
        .order('submitted_at', { ascending: true });

      if (approvalsError) throw approvalsError;
      const approvals = (allApprovals || []).filter(s => !s.public_reference?.startsWith('TM-'));
      setPendingApprovals(approvals);

      // Get projects awaiting admin review
      const { data: projectReviews, error: projectsError } = await supabase
        .from('projects')
        .select('id, project_name, project_lead_name, project_lead_email, submitted_at, depots:site_id(name)')
        .eq('site_id', user.depot_id)
        .eq('status', 'submitted_awaiting_approval')
        .order('submitted_at', { ascending: true });

      if (!projectsError) setPendingProjectReviews(projectReviews || []);

      // Get open incidents count
      const { count: incidentsCount, error: incidentsError } = await supabase
        .from('incidents')
        .select('*', { count: 'exact', head: true })
        .eq('depot_id', user.depot_id)
        .in('status', ['open', 'investigating']);

      if (incidentsError) throw incidentsError;
      setOpenIncidents(incidentsCount || 0);

      // Get today's check-ins count
      const today = new Date().toISOString().split('T')[0];
      const { count: todayCount, error: todayError } = await supabase
        .from('site_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('depot_id', user.depot_id)
        .gte('checked_in_at', `${today}T00:00:00`)
        .lt('checked_in_at', `${today}T23:59:59`);

      if (todayError) throw todayError;
      setTodayCheckIns(todayCount || 0);

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const KPICard = ({ icon: Icon, title, value, subtitle, color, onClick }) => (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={onClick}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-5 w-5 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  if (!user?.depot_id && !loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-foreground">No site assigned</p>
          <p className="mt-2 text-sm text-muted-foreground">Your account is not yet linked to a site. Contact your system administrator to be assigned to a depot.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="p-6 space-y-6 min-h-full"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.80), rgba(255, 255, 255, 0.80)), url('/assets/ATC-PHOTO-78.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Today at a Glance</h1>
        <p className="text-muted-foreground mt-1">
          {new Date().toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Users}
          title="Currently On Site"
          value={occupancy.total}
          subtitle={`${occupancy.employees} employees • ${occupancy.contractors} contractors • ${occupancy.visitors} visitors`}
          color="text-green-500"
          onClick={() => navigate(ROUTES.APP.ROLL_CALL)}
        />
        
        <KPICard
          icon={CheckSquare}
          title="Pending Approvals"
          value={pendingApprovals.length}
          subtitle={pendingApprovals.length === 0 ? 'All caught up!' : 'Awaiting review'}
          color="text-blue-500"
          onClick={() => navigate(ROUTES.APP.VISITORS)}
        />
        
        <KPICard
          icon={AlertTriangle}
          title="Open Incidents"
          value={openIncidents}
          subtitle={openIncidents === 0 ? 'No active incidents' : 'Require attention'}
          color="text-orange-500"
          onClick={() => navigate(ROUTES.APP.INCIDENTS)}
        />
        
        <KPICard
          icon={TrendingUp}
          title="Today's Check-ins"
          value={todayCheckIns}
          subtitle="Total visits today"
          color="text-purple-500"
          onClick={() => navigate(ROUTES.APP.DASHBOARD)}
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button 
            className="w-full"
            onClick={() => navigate(ROUTES.APP.QR_CODES)}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Generate QR Code
          </Button>
          
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(ROUTES.APP.VISITORS)}
          >
            <Users className="mr-2 h-4 w-4" />
            Invite Visitor
          </Button>
          
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(ROUTES.APP.INCIDENTS)}
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Report Incident
          </Button>
        </CardContent>
      </Card>

      {/* Action Items — always visible */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Site Approvals */}
        <Card className={pendingApprovals.length > 0 ? 'border-amber-300 shadow-md' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-primary" />
                Site Approvals
                {pendingApprovals.length > 0 && (
                  <span className="ml-1 flex items-center justify-center min-w-[22px] h-[22px] text-xs font-bold rounded-full bg-primary text-black">
                    {pendingApprovals.length}
                  </span>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.APP.VISITORS)}>
                View All <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pendingApprovals.length === 0 ? (
              <div className="flex items-center gap-3 py-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm">All caught up — no pending site approvals.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingApprovals.slice(0, 5).map((approval) => (
                  <div
                    key={approval.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                    onClick={() => navigate(ROUTES.APP.VISITORS)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{approval.person_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {approval.person_company || approval.person_type}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <Clock className="inline h-3 w-3 mr-1" />
                        Submitted {new Date(approval.submitted_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <Button size="sm" className="ml-3 flex-shrink-0">Review</Button>
                  </div>
                ))}
                {pendingApprovals.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">+{pendingApprovals.length - 5} more</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Reviews */}
        <Card className={pendingProjectReviews.length > 0 ? 'border-blue-300 shadow-md' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                Site Project Reviews
                {pendingProjectReviews.length > 0 && (
                  <span className="ml-1 flex items-center justify-center min-w-[22px] h-[22px] text-xs font-bold rounded-full bg-primary text-black">
                    {pendingProjectReviews.length}
                  </span>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.APP.PROJECTS)}>
                View All <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pendingProjectReviews.length === 0 ? (
              <div className="flex items-center gap-3 py-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm">No projects awaiting review.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingProjectReviews.map((proj) => (
                  <div
                    key={proj.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                    onClick={() => navigate(ROUTES.APP.PROJECTS)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{proj.project_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Lead: {proj.project_lead_name || proj.project_lead_email}
                      </p>
                      {proj.submitted_at && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <Clock className="inline h-3 w-3 mr-1" />
                          Submitted {new Date(proj.submitted_at).toLocaleDateString('en-GB')}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="outline" className="ml-3 flex-shrink-0">Review</Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Currently On Site Preview */}
      {activeSessions.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Currently On Site</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(ROUTES.APP.ROLL_CALL)}
              >
                View Full List →
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeSessions.slice(0, 5).map((session) => (
                <div 
                  key={session.id} 
                  className="flex items-center justify-between p-2 bg-muted rounded-md"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      session.person_type === 'employee' ? 'bg-green-500' :
                      session.person_type === 'contractor' ? 'bg-blue-500' :
                      'bg-purple-500'
                    }`} />
                    <div>
                      <p className="font-medium text-sm">{session.person_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {session.person_company || session.person_type} • {session.work_area}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(session.checked_in_at).toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              ))}
              {activeSessions.length > 5 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  +{activeSessions.length - 5} more people on site
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DepotHome;
